import { Component, inject, ChangeDetectionStrategy, forwardRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { BoxWebhookSetupDialogComponent } from '../box-webhook-setup-dialog/box-webhook-setup-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  UpdatePlanRequest,
  JobSource,
  ExecPlan,
} from '../../shared/interfaces/execution-plan.interfaces';
import {
  BaseRoutineDialogComponent,
  BaseRoutineDialogData,
} from '../../shared/base-routine-dialog/base-routine-dialog.component';
import { CodeEditorComponent } from '../../shared/modules/code-editor/code-editor.component';

export interface EditRoutineDialogData extends BaseRoutineDialogData {
  routine: ExecPlan;
}

@Component({
  selector: 'app-edit-routine-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCheckboxModule,
    // forwardRef breaks an NG0919 circular import: opened via FeatureDialogService
    // (imported by DialogService), and CodeEditorComponent injects DialogService
    // (SCRUM-718, follow-up to SCRUM-689).
    forwardRef(() => CodeEditorComponent),
  ],
  templateUrl: './edit-routine-dialog.component.html',
  styleUrls: ['./edit-routine-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditRoutineDialogComponent extends BaseRoutineDialogComponent {
  private dialogRef = inject(MatDialogRef<EditRoutineDialogComponent>);
  private readonly matDialog = inject(MatDialog);
  private formPopulated = false;
  /** Arguments editor content as populated, for changed-only PATCH semantics. */
  private originalArgumentsDisplay = '';

  // Injected data
  public data = inject<EditRoutineDialogData>(MAT_DIALOG_DATA);

  /** A saved Box webhook routine can have its Box webhook provisioned (SCRUM-614). */
  canSetUpBoxWebhook(): boolean {
    return (
      this.isWebhookSource() &&
      this.routineForm.get('webhookType')?.value === 'Box' &&
      !!this.data.routine?.routineId
    );
  }

  /** Open the Box webhook setup dialog for this routine. */
  openBoxWebhookSetup(): void {
    this.matDialog.open(BoxWebhookSetupDialogComponent, {
      data: { projectId: this.data.projectId, routineId: this.data.routine.routineId },
      width: '640px',
      maxWidth: '95vw',
    });
  }

  protected override onInitHook(): void {
    // Try to populate form immediately if routine data is available
    this.tryPopulateForm();

    this.routineForm
      .get('useHead')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((useHead: boolean) => {
        const targetVersionId = useHead
          ? this.getHeadVersionId()
          : (this.data.routine?.versionId ?? this.getLatestVersionId());
        if (targetVersionId != null) {
          this.loadProjectWorkflows(targetVersionId);
        }
      });
  }

  protected override addCustomFormControls(form: FormGroup): void {
    form.addControl('paused', this.fb.control(false));
  }

  protected override onProjectDataLoaded(): void {
    // Populate form after project data is loaded
    this.tryPopulateForm();
    const isHead = !!this.routineForm.get('useHead')?.value;
    const targetVersionId = isHead
      ? this.getHeadVersionId()
      : (this.data.routine?.versionId ?? this.getLatestVersionId());
    if (targetVersionId != null) {
      this.loadProjectWorkflows(targetVersionId);
    }
  }

  override getWorkflowNameById = (
    workflowId: number | null | undefined,
  ): string => {
    if (workflowId == null) return '';
    const name = this.DisplayUtils.getWorkflowNameById(
      workflowId,
      this.projectWorkflows,
    );
    if (name) return name;
    if (
      this.data.routine?.workflowId === workflowId &&
      this.data.routine?.workflowName
    ) {
      return this.data.routine.workflowName;
    }
    return '';
  };

  private tryPopulateForm(): void {
    // Only populate if we have routine data, the form is ready, and we haven't populated yet
    if (this.data.routine && this.routineForm && !this.formPopulated) {
      this.populateFormWithRoutineData();
    }
  }

  private populateFormWithRoutineData(): void {
    const routine = this.data.routine;

    // Ensure we have the routine data
    if (!routine) {
      return;
    }

    // Prepare form data with all fields
    const formData: any = {
      name: routine.name || '',
      description: routine.description || '',
      runsAsId: routine.runsAsId ?? null,
      workflowId: routine.workflowId ?? null,
      crontab: routine.crontab || '',
      cronScheduleType: '', // Will be set below
      paused: routine.paused || false,
      source:
        routine.source === JobSource.Webhook
          ? 'webhook'
          : routine.source === JobSource.Scheduled ||
              routine.source === JobSource.Triggered
            ? 'scheduled'
            : 'manual',
      webhookType: routine.webhookType || null,
      webhookSecretId1: routine.webhookSecretId1 || null,
      webhookSecretId2: routine.webhookSecretId2 || null,
      arguments: this.formatArgumentsForDisplay(routine.arguments),
      useHead: routine.useHead || false,
    };
    this.originalArgumentsDisplay = formData.arguments;

    // Set all form values
    this.routineForm.patchValue(formData);

    // Handle cron schedule type based on the crontab value
    this.setCronScheduleType(routine.crontab);

    // Force form to update the UI
    this.routineForm.updateValueAndValidity();

    // Mark form as populated
    this.formPopulated = true;
  }

  private setCronScheduleType(crontab: string | undefined): void {
    if (!crontab || crontab.trim() === '') {
      this.routineForm.get('cronScheduleType')?.setValue('');
      this.showCustomCronInput = false;
      return;
    }

    // Check if crontab matches any predefined option
    const matchingOption = this.cronScheduleOptions.find(
      (opt: any) => opt.value === crontab,
    );
    if (matchingOption) {
      this.routineForm.get('cronScheduleType')?.setValue(crontab);
      this.showCustomCronInput = false;
    } else {
      // It's a custom cron expression
      this.routineForm.get('cronScheduleType')?.setValue('custom');
      this.showCustomCronInput = true;
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.validateForm()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      const updateRequest = this.createUpdateRequestFromForm(
        this.routineForm.value,
      );

      this.executionPlanService
        .updatePlan(
          this.data.projectId,
          this.data.routine.routineId,
          updateRequest,
        )
        .subscribe({
          next: (result: any) => {
            this.loading.set(false);
            this.dialogRef.close(result);
          },
          error: (error: any) => {
            this.loading.set(false);
            this.error.set(this.ErrorHandlingUtils.getErrorMessage(error));
          },
        });
    } catch (error: any) {
      this.loading.set(false);
      this.error.set(error.message || 'Failed to update routine request');
    }
  }

  private createUpdateRequestFromForm(formValue: any): UpdatePlanRequest {
    const isWebhook = formValue.source === 'webhook';
    const hasCrontab = !isWebhook && formValue.crontab && formValue.crontab.trim() !== '';
    const source = isWebhook
      ? JobSource.Webhook
      : hasCrontab
        ? JobSource.Scheduled
        : JobSource.Manual;

    const request: UpdatePlanRequest = {
      name: formValue.name,
      description: formValue.description || '',
      runsAsId: formValue.runsAsId,
      paused: formValue.paused,
      source,
    };

    if (hasCrontab) {
      request.crontab = formValue.crontab;
    } else {
      request.crontab = '';
    }

    // Include webhook fields for webhook routines
    if (isWebhook) {
      request.webhookType = formValue.webhookType;
      request.webhookSecretId1 = formValue.webhookSecretId1 ?? undefined;
      request.webhookSecretId2 = formValue.webhookSecretId2 ?? undefined;
    }

    request.useHead = !!formValue.useHead;

    // SCRUM-698: the Edit Routine dialog now lets users change the workflow.
    // Backend enforces mutual exclusion of workflowId / workflowName via a CHECK
    // constraint, so we never send both. Branching:
    //   - useHead: always send workflowName (resolved from the current form
    //     selection) so the backend re-pins against HEAD at run time.
    //   - non-useHead: send workflowId only when the user actually changed
    //     the selection, preserving PATCH semantics for unchanged edits.
    const originalWorkflowId = this.data.routine.workflowId ?? null;
    const formWorkflowId =
      (formValue.workflowId ?? null) as number | null;
    const workflowChanged = formWorkflowId !== originalWorkflowId;

    if (formValue.useHead) {
      const effectiveWorkflowId = formWorkflowId ?? originalWorkflowId;
      const resolvedWorkflowName =
        effectiveWorkflowId != null
          ? this.getWorkflowNameById(effectiveWorkflowId)
          : null;
      const workflowName = resolvedWorkflowName || this.data.routine.workflowName;

      if (workflowName) {
        request.workflowName = workflowName;
      }
    } else if (workflowChanged && formWorkflowId != null) {
      request.workflowId = formWorkflowId;
    }

    // PATCH semantics: only send arguments when the user changed them.
    // Sending "{}" clears the routine's stored args; omitting leaves them
    // untouched on the server.
    const currentArguments = (formValue.arguments ?? '').trim();
    if (currentArguments !== this.originalArgumentsDisplay.trim()) {
      request.arguments = currentArguments === '' ? '{}' : currentArguments;
    }

    return request;
  }
}
